<template>
  <div>
    <div class="mc-editable-table">
      <div :class="hasMargin ? 'clearfix mb-4' : 'clearfix mb-0'">
        <div class="d-flex mc-list-header">
          <h4 class="card-title flex-grow-1 d-flex align-items-center">
            {{ title ? title : $t('notification.setting.emailNotification.recipientList') }}
          </h4>
          <slot name="title" />
          <McButton
            v-if="($route.params.id || !hasEmptyState) && selectedIdsLength"
            variant="secondary-red"
            class="mx-2 ms-auto"
            icon="icon-outline_trash"
            :text="$t('global.delete')"
            @click="$refs['mc-modal-delete'].show()"
          />

          <McButton
            variant="primary"
            class="ms-auto"
            :text="$t('global.create')"
            icon="icon-fill_plus"
            :disabled="disabled || disabledGlobal"
            @click="handleAdd"
          />
        </div>
      </div>

      <div v-if="!isEmpty || !hasEmptyState" :class="{ card: withCard, 'p-4': cardPadding }">
        <b-row>
          <b-col md="4" class="mc-row-check-wrapper">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0 mb-2">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="handleUnselectAll">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
            <McAlert
              v-if="invalidKeysLength && !selectedIdsLength"
              variant="error"
              text="Baris data error."
            >
              <template #prefix>
                <div class="mc-alert__custom_prefix">
                  <p>{{ invalidKeysLength }}</p>
                </div>
              </template>
            </McAlert>
          </b-col>
        </b-row>

        <FormulateForm
          ref="formRef"
          v-model="formValues"
          name="editable-table-form"
          :errors="formulateFormErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div
            :class="`table-responsive border border-1 mc-loads-table__editable-table ${
              selectedIdsLength ? '' : 'mt-4'
            }`"
          >
            <b-table
              v-if="!isLoading"
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              :per-page="limit"
              fixed
              show-empty
              class="editable-table mc-editable-table--multiselect mc-table-fleetdoc mc-contact-table-global"
            >
              <!-- Loading -->
              <template #table-busy>
                <div class="__busy">
                  <b-spinner class="align-middle mb-2" />
                  <strong>{{ $t('global.text.loading') }}...</strong>
                </div>
              </template>

              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <McCheckbox
                  v-model="selectedAll"
                  :disabled="disabledGlobal || items.length === 0"
                  @change="handleSelectAll"
                />
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div>
                      <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up"></i>
                      </div>
                      <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <McCheckbox
                  v-model="selectedIds[data.item.contactId]"
                  :disabled="disabledGlobal"
                  @change="(event) => handleSelect(event, data)"
                />
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                  <FormulateInput
                    v-if="field.type === 'phone-number'"
                    :ref="`${field.key}-ref`"
                    v-bind="{ ...field }"
                    :rowItem="item"
                    :label="null"
                    :name="field.inputName || field.key"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting || field.disabled"
                    size="sm"
                    :validation-behavior="field.validationBehavior"
                    :error-behavior="field.errorBehavior"
                    @countryCode="getCountryCode"
                  />

                  <FormulateInput
                    v-else-if="field.type === 'select-resourceful-v2'"
                    v-bind="{ ...field }"
                    :fullObject="fullObjects[field.name]"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    :validation-behavior="field.validationBehavior"
                    :error-behavior="field.errorBehavior"
                    size="sm"
                    @update:fullObject="($event) => setFullObject(field.name, $event)"
                    @addTag="customTag"
                  />

                  <FormulateInput
                    v-else
                    v-bind="{ ...field }"
                    :fullObject="fullObjects[field.name]"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    :validation-behavior="field.validationBehavior"
                    :error-behavior="field.errorBehavior"
                    size="sm"
                    @update:fullObject="($event) => setFullObject(field.name, $event)"
                  />
                </template>

                <!-- Bold First Column -->
                <template v-else-if="field.isRedirectable">
                  <div class="fw-bold text-primary">
                    {{ value }}
                  </div>
                </template>

                <!-- Show Only Column -->
                <template v-else-if="!field.isEditable && isEditingRow == index">
                  <div>
                    {{ transformCellValue(value, field) }}
                  </div>
                </template>

                <template v-else>
                  <div
                    class="td__overflow-ellipsis"
                    :class="
                      invalidItems[item.id] && invalidItems[item.id][field.name]
                        ? 'td__invalid-data'
                        : undefined
                    "
                  >
                    {{ transformCellValue(value, field, item) }}
                  </div>
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <b-button variant="outline" class="__accept-button btn" type="submit">
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: isEditingRow !== null || isBusy || !hasScope,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </template>
            </b-table>
          </div>
        </FormulateForm>

        <div class="d-flex flex-wrap flex-md-nowrap mt-4">
          <div class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.show') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="disabledGlobal"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.object.data') }}.</div>
            </div>

            <div v-if="totalCount === 0" class="__info">
              <strong>{{ totalCount }}</strong> {{ $t('global.object.from') }}
              <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}.
            </div>

            <div v-else class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.from') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.object.data') }}.
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="disabledGlobal"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </div>

      <div v-else :class="{ card: withCard }">
        <McEmptyState
          :title="emptyState"
          :desc="emptyStateDesc"
          class="pb-4"
          :placementType="emptyState ? 'grid' : 'list'"
          :hasButton="false"
        />
      </div>
    </div>
    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      :title="$t('global.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McEmptyState from '@/components/McEmptyState'
import parsePhoneNumber from 'libphonenumber-js'
import { mapGetters } from 'vuex'
import McCheckbox from '@/components/McCheckbox'

export default {
  components: {
    McButton,
    McAlert,
    McEmptyState,
    McCheckbox,
  },

  mixins: [Helper],
  props: {
    apiClient: {
      required: true,
    },
    limitOptions: {
      default: () => [5, 10, 25, 50, 100],
    },
    // invalidFields: { type: Array },
    fields: {
      type: Array,
    },
    hasData: {
      type: Boolean,
    },
    listDatas: { type: Array },
    disabled: {},
    withCard: {
      default: true,
    },
    cardPadding: {
      default: true,
    },
    emptyState: {},
    emptyStateDesc: {},
    title: {
      type: String,
      required: false,
    },
    hasEmptyState: {
      type: Boolean,
      default: true,
    },
    bulkCreate: {
      type: Boolean,
      default: false,
    },
    hasMargin: {
      type: Boolean,
      default: true,
    },
    dataStore: {
      type: Array,
      default: () => [],
    },
    hasScope: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isBusy: true,
      isSubmitting: false,
      limit: this.limitOptions[0] ?? 10,
      page: 1,
      totalCount: 0,
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},
      isAdding: null,
      loadsData: {},
      showResponse: {},
      invalidItems: {},
      formulateFormErrors: {},
      fullObjects: {
        user: {},
      },
      render: true,
      isEmpty: !this.hasData,
      initialCountryCode: '62',
      countryCode: '62',
      isLoading: false,
    }
  },

  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
          thStyle: 'width: 40px',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
    hasWhatsapp() {
      return this.hasAccessAll(['whatsapp-integration:read'])
    },
  },
  watch: {
    async formValues(newValue, oldValue) {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(this.formValues, this.loadsData)

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.user': async function (newValue, oldValue) {
      if (!newValue) {
        return
      }

      if (newValue === oldValue) {
        return
      }

      if (newValue !== oldValue) {
        if (typeof newValue == 'string') {
          return
        } else {
          const valueData = this.listDatas.filter((item) => item.userId == newValue)

          let data = valueData[0]
          if (this.fullObjects.user || !data) {
            data = this.fullObjects.user
          }

          const phoneNumber = data.phoneNumber ? data.phoneNumber : data.phoneNo

          if (data.userId !== 9999999) {
            if (this.hasWhatsapp) {
              if (phoneNumber)
                this.$set(this.formValues, 'phoneNumber', this.trimPhoneNo(phoneNumber))
              if (!phoneNumber) this.$set(this.formValues, 'phoneNumber', null)
            } else {
              this.$set(this.formValues, 'phoneNumber', null)
            }
            if (!data.email) this.$set(this.formValues, 'email', null)
            else this.$set(this.formValues, 'email', data.email ?? this.formValues.email)
          }
        }
      }
    },
    countryCode: function (value) {
      if (!value) {
        this.countryCode = this.initialCountryCode
      }
    },
  },

  async mounted() {
    this.fields.forEach((el) => {
      if (el.isSortable) el._sort = 'desc'
      if (el.orderBy) this.sorts.push({ [el.key]: el.orderBy })
    })

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      sorts: this.stringSorts(),
    })
  },

  methods: {
    trimPhoneNo(value) {
      if (!value) return

      let noTelp = value
      if (value[0] == '0') noTelp = this.initialCountryCode + value.slice(1, value.length)

      if (!this.countryCode) this.countryCode = this.initialCountryCode
      noTelp = value.includes(this.countryCode) ? value : this.countryCode + value

      let phoneNumber = parsePhoneNumber(`+${noTelp.toString()}`)

      const countryCode = phoneNumber?.countryCallingCode
      this.countryCode = countryCode

      this.$emit('updateCountryCode', this.countryCode)

      return phoneNumber?.nationalNumber
    },

    stringSorts() {
      return this.sorts.reduce((next, ob) => {
        const str = JSON.stringify(ob)
        return [...next, str]
      }, [])
    },
    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    transformCellValue(value, field, item) {
      if (value === false) return '-'

      if (field.name === 'phoneNumber' && value) {
        const phoneNumber = parsePhoneNumber(`+${value.toString()}`)
        return phoneNumber?.number
      }

      if (field.name === 'user') {
        if (value) return value
        else return item.label
      }
      if (typeof value === 'number') return value.toLocaleString('id-ID')
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: null }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },

    async getData(opts) {
      if (this.bulkCreate) {
        const contacts = this.dataStore
        this.items = contacts
        this.totalCount = contacts.length
        this.isEmpty = contacts.length === 0
        this.isBusy = false
        return
      }

      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        search: opts.search,
      }

      this.isBusy = true
      const res = await this.apiClient.get(filter)

      if (!res) return

      this.items = res.data
      if (this.items.length > 0) this.isEmpty = false
      else this.isEmpty = true

      if (this.items.length == 0 && this.addData && !opts.search) this.handleAdd()

      this.ids = res.data
      this.page = res.pagination.page
      this.totalCount = res.pagination.total
      if (this.selectedAll) this.handleUnselectAll()

      this.isBusy = false
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
      this.$refs['searchInput'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
    },

    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      })
    },

    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
    },

    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },

    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (!this.hasScope) return
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.getById(data.item.contactId)
      if (!refetchedData) return

      const loadData = refetchedData.contact

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.contactId == data.item.contactId) return loadData
        return item
      })

      // assign formValues with response show data
      this.fields.forEach((field) => {
        if (!field.isEditable) return
        let value
        if (field.name === 'phoneNumber') value = this.trimPhoneNo(loadData.phoneNumber)
        else if (field.name === 'user')
          value = loadData.label && !loadData.userId ? 9999999 : loadData.userId
        else value = loadData[field.name]

        this.fullObjects.user = {
          email: loadData.email ?? null,
          fullname: loadData.userId ? loadData.user.fullname : loadData.label,
          phoneNumber: loadData.phoneNumber ?? null,
          userId: loadData.label && !loadData.userId ? 9999999 : loadData.userId,
          contactId: loadData.contactId,
          label: loadData.label,
        }

        this.$set(this.formValues, field.name, value)
      })

      this.isEditingRow = data.index
      this.showResponse = { ...loadData }

      this.loadsData = this.formValues

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[loadData.contactId]) {
        this.formulateFormErrors = { ...this.invalidItems[loadData.contactId] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}

      this.isEditingRow = null
    },
    async submitStore(data) {
      if (this.bulkCreate) {
        // Handle bulk mode storage
        const tag = {
          ...this.fullObjects.user,
          contactId: this.items.length + 1,
          phoneNo: data.phoneNumber ? this.countryCode + data.phoneNumber : null,
          email: data.email,
        }

        const createdData = await this.apiClient.store(tag)

        this.items = [createdData.createContact, ...this.items.map((item) => ({ ...item }))]
        this.totalCount = this.items.length

        // Clear form state
        this.formValues = {}
        this.fullObjects = { user: {} }
        this.countryCode = this.initialCountryCode

        // Refresh table data
        await this.getData()

        return createdData.createContact
      } else {
        const createdData = await this.apiClient.store(data, this.fullObjects.user)

        if (!createdData.isSuccessful) {
          if (createdData.errorCode == 'E-C1-H1/2')
            this.$mcToast.warning(this.$t('notification.setting.emailNotification.duplicatedEmail'))
          else if (createdData.errorCode == 'E-C1-H1/3' || createdData.errorCode == 'E-C1-H1/9')
            this.$mcToast.warning(
              this.$t('notification.setting.emailNotification.duplicatedContact')
            )
          else if (createdData.errorCode == 'E-C1-H1/1')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.userNotExists'))
          else if (createdData.errorCode == 'E-C1-H1/4')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.reportSchedulerNotExists'))
          else if (createdData.errorCode == 'E-C1-H1/5' || createdData.errorCode == 'E-C1-H1/8')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.duplicatedEmail'))
          else if (createdData.errorCode == 'E-C1-H1/6')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.duplicatedContact'))
          else if (createdData.errorCode == 'E-C1-C1/1')
            this.$mcToast.warning(this.$t('notification.setting.emailNotification.mustbefilled'))
          else this.$mcToast.warning(this.$t('global.alert.text.addDataFailed'))

          return false
        }
        this.$mcToast.success(this.$t('global.alert.text.added'))

        const refetchedData = createdData
        if (!refetchedData) return false

        this.$set(this.fullObjects, 'user', refetchedData.createContact.user)
        this.ids = this.ids.slice(0, this.limit - 1)

        this.ids.unshift(refetchedData.createContact)

        this.totalCount += 1

        return {
          ...data,
          ...refetchedData.createContact,
        }
      }
    },
    async submitUpdate(data, oldData) {
      if (this.bulkCreate) {
        // Handle bulk mode storage
        const tag = {
          ...this.fullObjects.user,
          contactId: this.items.length + 1,
          phoneNumber: data.phoneNumber ? this.countryCode + data.phoneNumber : null,
          email: data.email,
        }

        const updatedData = await this.apiClient.update(oldData.contactId, tag, oldData)

        this.items = [updatedData.updateContact, ...this.items.map((item) => ({ ...item }))]
        this.totalCount = this.items.length

        // Clear form state
        this.formValues = {}
        this.fullObjects = { user: {} }
        this.countryCode = this.initialCountryCode

        // Refresh table data
        await this.getData()

        return updatedData.updateContact
      } else {
        const [patchData, hasChanged] = this.filterUnchangedData(data, oldData)

        if (!hasChanged) {
          this.isSubmitting = false
          this.handleEditCancel({ index: this.isEditingRow })
          return
        }

        const updatedData = await this.apiClient.update(
          oldData.contactId,
          data,
          this.fullObjects.user
        )
        const refetchedData = updatedData

        if (!refetchedData.isSuccessful) {
          if (refetchedData.errorCode == 'E-C1-H1/2')
            this.$mcToast.warning(this.$t('notification.setting.emailNotification.duplicatedEmail'))
          else if (refetchedData.errorCode == 'E-C1-H1/4')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.reportSchedulerNotExists'))
          else if (refetchedData.errorCode == 'E-C1-H1/5' || refetchedData.errorCode == 'E-C1-H1/8')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.duplicatedEmail'))
          else if (refetchedData.errorCode == 'E-C1-H1/1')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.userNotExists'))
          else if (refetchedData.errorCode == 'E-C1-H1/6')
            this.$mcToast.warning(this.$t('dashboard.scheduler.text.duplicatedContact'))
          else if (refetchedData.errorCode == 'E-C1-H1/3' || refetchedData.errorCode == 'E-C1-H1/9')
            this.$mcToast.warning(
              this.$t('notification.setting.emailNotification.duplicatedContact')
            )
          else if (refetchedData.errorCode == 'E-C1-U1/2')
            this.$mcToast.warning(this.$t('notification.setting.emailNotification.mustbefilled'))
          else this.$mcToast.warning(this.$t('global.alert.text.failedEdit'))

          return false
        }

        this.loadsData = refetchedData.updateContact

        if (this.invalidItems[oldData.contactId]) {
          this.$delete(this.invalidItems, oldData.contactId)
          this.formulateFormErrors = {}
        }

        this.$mcToast.success(this.$t('global.alert.text.changed'))

        return {
          ...refetchedData.updateContact,
        }
      }
    },
    async handleSubmit(data) {
      this.isLoading = true

      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        if (!this.formValues.email && !this.formValues.phoneNumber) {
          this.$mcToast.warning(this.$t('notification.setting.emailNotification.mustbefilled'))
          this.isSubmitting = false
          this.isLoading = false
          this.isEditingRow = 0
          this.isAdding = true
          return
        }

        newData = await this.submitStore(transformedData)
        // If newData is false, it means there is an error
        if (newData === false) {
          this.isSubmitting = false
          this.isLoading = false
          this.isEditingRow = null
          this.items.splice(0, 1)
          this.isAdding = false
          return
        }
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
      this.isLoading = false
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      if (this.bulkCreate) {
        const itemsToDelete = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])

        // Remove from Vuex store
        this.$store.commit('dashboard/DELETE_CONTACT', itemsToDelete)

        // Update local table data
        this.items = this.items.filter((item) => !itemsToDelete.includes(item.contactId.toString()))
        this.totalCount = this.items.length

        // Reset selection state
        this.handleUnselectAll()
        this.$refs['mc-modal-delete'].hide()
      } else {
        const promises = []
        for (let key in this.selectedIds) {
          if (this.selectedIds[key]) {
            promises.push(this.apiClient.delete(key))
          }
        }
        await Promise.all(promises)
        if (this.search) this.handleClearSearch()
        await this.getData({
          limit: this.limit,
          page: 1,
        })

        for (const selectedid in this.selectedIds) {
          if (this.invalidItems[selectedid]) {
            this.$delete(this.invalidItems, selectedid)
          }
        }

        await this.$nextTick()
        this.handleSortChange()
        this.handleUnselectAll()
        this.$refs['mc-modal-delete'].hide()
        this.$mcToast.success(this.$t('global.alert.text.delete'))
      }
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.contactId]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(
          this.ids.map((data) => [typeof data === 'number' ? data : data.contactId, true])
        )
        this.selectedIdsLength = this.ids.length
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.isEmpty = false
      this.items = [
        {
          email: null,
          label: null,
          phoneNumber: null,
          user: {
            fullname: null,
            label: null,
            userId: 0,
          },
          userId: 0,
        },
        ...this.items,
      ]
    },

    customTag(value) {
      const tag = {
        fullname: value,
        label: value,
        userId: 9999999,
        email: this.formValues.email,
        phoneNumber: this.formValues.phoneNumber,
      }

      this.$emit('customTag', tag)
      this.setFullObject('user', tag)
      this.$set(this.formValues, 'user', tag.userId)
      this.$set(this.formValues, 'phoneNumber', tag.phoneNumber ?? null)
      this.$set(this.formValues, 'email', tag.email ?? null)
    },

    setFullObject(field, value) {
      this.fullObjects[field] = value
    },

    getCountryCode(value) {
      this.countryCode = value
      this.$emit('updateCountryCode', value)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
